import { Button, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExpenseQuery } from "web_myplann/Api/Finances/useExpenseQuery";
import { useIncomeQuery } from "web_myplann/Api/Finances/useIncomeQuery";
import { useLeadMutation } from "web_myplann/Api/Finances/useLeadMutation";
import { DashboardSkeleton } from "web_myplann/Dashboard/Components/DashboardSkeleton";
import { FaultyFinancialBalance } from "web_myplann/Dashboard/Components/FaultyFinancialBalance";
import { useOnlineStatus } from "web_myplann/Infrastructure/Hooks/useOnlineStatus";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { Colors } from "web_myplann/Infrastructure/Themes/Colors";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ContactAgentDialog } from "web_myplann/Shared/Components/ContactAgentDialog";
import { MyplannPieChartIcon } from "web_myplann/Shared/Icons";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import { GRASS_GREEN_MYPLANN } from "web_myplann/Utils/constants";

const PageResources = Resources.Dashboard.FinancialBalance;

export const FinancialBalance = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();

	const isOnline = useOnlineStatus();

	const { data: expenses, isLoading: isExpensesLoading } = useExpenseQuery();
	const { data: income, isLoading: isIncomeLoading } = useIncomeQuery();

	const isLoading = isExpensesLoading || isIncomeLoading;
	const { mutate: makeLead } = useLeadMutation();

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const noData = !Array.isArray(expenses?.data) && !Array.isArray(income?.data);

	if (noData && !isLoading) {
		return <FaultyFinancialBalance />;
	}

	const onContactAgent = () => {
		setIsDialogOpen(false);
		const expensesSum =
			expenses?.data?.reduce(
				(acc, curr) => acc + (curr?.monthlyPayment ?? 0),
				0,
			) ?? 0;
		const incomeSum =
			income?.data?.reduce((acc, curr) => acc + (curr?.amount ?? 0), 0) ?? 0;

		makeLead({
			expense: expensesSum,
			income: incomeSum,
		});
	};

	const balance =
		(income?.data?.reduce((acc, curr) => acc + (curr?.amount ?? 0), 0) ?? 0) -
		(expenses?.data?.reduce(
			(acc, curr) => acc + (curr?.monthlyPayment ?? 0),
			0,
		) ?? 0);

	const hasNoBalance =
		income?.data?.length === 0 && expenses?.data?.length === 0;

	const handleNavigate = () => {
		const target = hasNoBalance
			? Routing.Finances.Income.path()
			: Routing.Finances.Intro.path();
		navigate(target, { state: { from: "dashboard", wizardDepth: 1 } });
	};

	if (isLoading) {
		return <DashboardSkeleton height={200} />;
	}

	return (
		<>
			{income?.data?.length === 0 && expenses?.data?.length === 0 ? (
				<Stack
					gap={1.6}
					sx={{
						background:
							"linear-gradient(to bottom,rgb(12, 90, 188),rgb(135, 204, 209))",
					}}
					px={1.6}
					borderRadius={"12px"}
				>
					<Stack pt={2.4}>
						<Typography
							variant="deprecatedH3"
							color={theme.palette.darkmodeText.white}
						>
							{t(PageResources.title)}
						</Typography>
					</Stack>
					<Stack direction="row" alignItems="center" gap={1.6}>
						<MyplannPieChartIcon />
						<Stack>
							<Typography
								variant="deprecatedBody1"
								color={theme.palette.darkmodeText.white}
							>
								{balance > 0
									? t(PageResources.descriptionPositive)
									: t(PageResources.descriptionNegative)}
							</Typography>
						</Stack>
					</Stack>
					<Stack pb={2.4}>
						<Button
							sx={{
								background: Colors.Primary300,
								color: theme.palette.deprecated.info.main,
								"&:enabled": {
									background: Colors.Primary300,
								},
							}}
							onClick={() => {
								handleNavigate();
							}}
						>
							{t(PageResources.startButton)}
						</Button>
					</Stack>
				</Stack>
			) : (
				<Stack
					gap={1.6}
					bgcolor={theme.palette.deprecated.grey[100]}
					p={1.6}
					borderRadius={"12px"}
				>
					<Stack
						alignItems="center"
						justifyContent="space-between"
						direction="row"
					>
						<Typography variant="deprecatedH3">
							{t(PageResources.title)}
						</Typography>
						<Stack direction="row">
							<IconButton onClick={handleNavigate}>
								<ChevronRight size={16} color={theme.palette.primary.main} />
							</IconButton>
						</Stack>
					</Stack>
					<Stack gap={0.8}>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
							textAlign="center"
						>
							{balance > 0
								? t(PageResources.subtitlePositive)
								: t(PageResources.subtitleNegative)}
						</Typography>
						<Stack alignItems="center">
							<Typography
								fontSize="32px"
								fontWeight={700}
								color={
									balance > 0
										? GRASS_GREEN_MYPLANN
										: theme.palette.deprecated.error.main
								}
							>
								{moneyFormatter(
									balance,
									t(Resources.Common.CurrencySign),
									`${balance} ${t(Resources.Common.CurrencySign)}`,
								)}
							</Typography>
						</Stack>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
							textAlign="center"
						>
							{balance > 0
								? t(PageResources.descriptionPositive)
								: t(PageResources.descriptionNegative)}
						</Typography>
					</Stack>
					<Button onClick={() => setIsDialogOpen(true)} disabled={!isOnline}>
						{t(PageResources.contactAdvisorButton)}
					</Button>
				</Stack>
			)}
			<ContactAgentDialog
				open={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
				onConfirm={onContactAgent}
			/>
		</>
	);
};
